import type { Key } from 'react-aria-components';

import { t } from '@lingui/core/macro';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

import type {
  AgeGroup,
  CountryCode,
  ScreenerResponse,
} from '@/api/core-api/types.gen';

import { ScreenerResponseSortField } from '@/api/core-api/types.gen';
import { AlertFullWidth } from '@/components/application/alerts/alerts';
import { DEFAULT_PAGE } from '@/components/application/table/table-page-search';
import { notify } from '@/components/base/notifications/notifications';
import { Tabs } from '@/components/base/tabs/tabs';
import { getProjectActionFlags } from '@/components/projects/project-action-visibility';
import { getTypeConfig } from '@/components/projects/project-type-badge';
import { queryKeys } from '@/hooks/api/query-keys';
import { organizationCanUseAiQueryOptions } from '@/hooks/api/use-organization';
import { useProjectAction } from '@/hooks/api/use-project-action';
import { useProjectBookings } from '@/hooks/api/use-project-bookings';
import { useProjectDetailsRealtime } from '@/hooks/api/use-project-details-realtime';
import { useProjectInvitations } from '@/hooks/api/use-project-invitations';
import { useProjectMessagesRealtime } from '@/hooks/api/use-project-messages-realtime';
import { useProjectPaymentsOverview } from '@/hooks/api/use-project-payments-overview';
import { useProjectUnreadCounts } from '@/hooks/api/use-project-unread-counts';
import { useQuotaQuery } from '@/hooks/api/use-quota-query';
import {
  useInfiniteParticipantsTable,
  useResearcherApplicantByUserId,
} from '@/hooks/api/use-researcher-applicants';
import { useResearcherCalendars } from '@/hooks/api/use-researcher-calendars';
import { useResearcherCompletes } from '@/hooks/api/use-researcher-completes';
import { useResearcherAllInterviewResponses } from '@/hooks/api/use-researcher-interview-responses';
import { useTeamCapabilities } from '@/hooks/api/use-team-capabilities';
import { useAnalytics } from '@/lib/analytics/use-analytics';
import { useFeatureFlag } from '@/lib/feature-flags/client/use-feature-flag';
import { isomorphicOrgFeatureFlagQueryOptions } from '@/lib/feature-flags/isomorphic-org-feature-flag-query-options';
import { getAxiosErrorMessage, getAxiosErrorStatus } from '@/utils/error';
import { getPublicEnv } from '@/utils/session/get-public-env';

import {
  isAiModStudy,
  isAvailabilityRequired,
  isModeratedStudy,
  isOnlineTaskProject,
  isQuantitativeStudy,
} from '../-study-type-helpers';
import { selectConfirmedBookings } from './-details/-bookings/bookings';
import { BookingsTab } from './-details/-bookings/bookings-tab';
import { AddCreditCardModal } from './-details/-participants/add-credit-card-modal';
import {
  nextSearchOnAgeFilterChange,
  PARTICIPANT_AGE_FILTER_VALUES,
  type ParticipantAgeFilter,
} from './-details/-participants/participant-age-filter';
import {
  nextSearchOnGenderFilterChange,
  PARTICIPANT_GENDER_FILTER_VALUES,
  type ParticipantGenderFilter,
} from './-details/-participants/participant-gender-filter';
import { nextSearchOnLocationFilterChange } from './-details/-participants/participant-location-filter';
import {
  PARTICIPANT_QUALIFICATION_URL_VALUES,
  type ParticipantQualificationFilter,
} from './-details/-participants/participant-qualification-filter';
import { nextSearchOnQuotaSegmentFilterChange } from './-details/-participants/participant-quota-segment-filter';
import {
  effectiveQualification,
  nextSearchOnQualificationFilterChange,
  nextSearchOnStatusFilterChange,
  nextSearchOnVisibilityFilterChange,
  toParticipantApiFilters,
} from './-details/-participants/participant-search';
import {
  PARTICIPANT_STATUS_FILTER_VALUES,
  type ParticipantStatusFilter,
} from './-details/-participants/participant-status-filter';
import {
  PARTICIPANT_VISIBILITY_URL_VALUES,
  type ParticipantVisibilityFilter,
} from './-details/-participants/participant-visibility-filter';
import { useInviteCoverage } from './-details/-participants/use-invite-coverage';
import { AnalyticsTab } from './-details/analytics-tab';
import { CalendarTab } from './-details/calendar-tab';
import { ClipsTab } from './-details/clips-tab';
import { CompletesTab } from './-details/completes-tab';
import { ConceptsTab } from './-details/concepts-tab';
import { DetailsTab } from './-details/details-tab';
import { FeaturesSchemaVersionBar } from './-details/features-schema-version-bar';
import { InsightsChatTab } from './-details/insights-chat/insights-chat-tab';
import { InsightsReportPanel } from './-details/insights-report/insights-report-panel';
import { MessagesTab } from './-details/messages-tab';
import {
  drawerCueKindFromDeliveryMode,
  InterviewDrawerCueProvider,
} from './-details/participant-display';
import { ParticipantsTab } from './-details/participants-tab';
import {
  shouldShowAutoPayBanner,
  shouldShowAvailabilityBanner,
  shouldShowLockedBanner,
} from './-details/participants-tab-banners';
import {
  nextSearchOnPayoutFilterChange,
  parsePayoutFilterValue,
  type PayoutCountFilterValue,
  payoutSearchSchema,
} from './-details/payments/-payout-rules';
import { PaymentsTab } from './-details/payments/payments-tab';
import { ResultsTab } from './-details/results-tab';
import { ScreenerResponsesDrawer } from './-details/screener-responses-drawer';
import { computeTabBadges } from './-details/tab-badges';
import { TabCountBadge } from './-details/tab-count-badge';
import {
  ALL_TAB_IDS,
  getAllowedTabs,
  type TabId,
  VIEWPORT_FILLING_TABS,
} from './-details/tab-ids';
import { useFeaturesSchemaVersionView } from './-details/use-features-schema-version-view';
import { InterviewResponsesDrawer } from './-interview-responses-drawer';
import { ageRangeFromProject } from './-launch/drawers/target-audience/age-range';
import { ProjectDetailsLayout } from './-project-details-layout';
import { isEncodedSegmentFilter } from './-quotas/quota-assignment';
import { useQuotasV2 } from './-quotas/use-quotas-v2';
import { researcherProjectQueryOptions } from './-shared';
import { ParticipantSideView } from './-side-panel/participant-side-view';

/** Tabs that use the shared `page` search param. Others drop it from the URL. */
const PAGINATED_TAB_IDS: ReadonlySet<TabId> = new Set([
  'participants',
  'results',
  'completes',
]);

function isPaginatedTab(tab: TabId): boolean {
  return PAGINATED_TAB_IDS.has(tab);
}

const detailsSearchSchema = z.object({
  // `.default()` only covers absence; `.catch()` covers invalid values (e.g.
  // `?tab=bogus` or a number) so a bad deep-link doesn't crash the route.
  tab: z.enum(ALL_TAB_IDS).catch('participants').default('participants'),
  // Optional (no default) so it can be dropped from the URL on tabs that don't
  // paginate. Absent → undefined (treated as page 1 at the read site); an
  // invalid value still falls back to page 1.
  page: z.coerce.number().int().positive().catch(DEFAULT_PAGE).optional(),
  /**
   * Participants tab status filter. Unknown entries are dropped element-wise
   * so a deep-link with mixed valid + retired statuses keeps the valid ones.
   */
  statuses: z
    .array(z.string())
    .optional()
    .transform((values): ParticipantStatusFilter[] | undefined => {
      if (!values) return;
      const valid = values.filter((v): v is ParticipantStatusFilter =>
        PARTICIPANT_STATUS_FILTER_VALUES.some((s) => s === v),
      );
      return valid.length === 0 ? undefined : valid;
    })
    .catch(undefined),
  /**
   * Participants tab sort. Default "QUALIFIED" omits from URL; "RECENT" is explicit.
   */
  sort: z.enum(['QUALIFIED', 'RECENT']).optional().catch(undefined),
  /**
   * Participants tab visibility view. The default "Show all" (visible roster)
   * omits from URL; "favorite" and "hidden" are explicit.
   */
  view: z.enum(PARTICIPANT_VISIBILITY_URL_VALUES).optional().catch(undefined),
  /**
   * Participants tab qualification filter. Qualified is the default when this
   * is absent; Show all and Not qualified are explicit overrides.
   */
  qual: z
    .enum(PARTICIPANT_QUALIFICATION_URL_VALUES)
    .optional()
    .catch(undefined),
  /**
   * Participants tab gender filter. Unknown entries are dropped element-wise.
   */
  genders: z
    .array(z.string())
    .optional()
    .transform((values): ParticipantGenderFilter[] | undefined => {
      if (!values) return;
      const valid = values.filter((v): v is ParticipantGenderFilter =>
        PARTICIPANT_GENDER_FILTER_VALUES.some((g) => g === v),
      );
      return valid.length === 0 ? undefined : valid;
    })
    .catch(undefined),
  /**
   * Participants tab age-bracket filter. Unknown entries are dropped
   * element-wise.
   */
  ageGroups: z
    .array(z.string())
    .optional()
    .transform((values): ParticipantAgeFilter[] | undefined => {
      if (!values) return;
      const valid = values.filter((v): v is ParticipantAgeFilter =>
        PARTICIPANT_AGE_FILTER_VALUES.some((g) => g === v),
      );
      return valid.length === 0 ? undefined : valid;
    })
    .catch(undefined),
  /**
   * Participants tab location (country) filter. Codes are uppercased ISO
   * alpha-2; empty selection omits from the URL.
   */
  countries: z
    .array(z.string())
    .optional()
    .transform((values): CountryCode[] | undefined => {
      if (!values) return;
      const valid = values
        .map((v) => v.trim().toUpperCase())
        .filter((v): v is CountryCode => /^[A-Z]{2}$/.test(v));
      return valid.length === 0 ? undefined : valid;
    })
    .catch(undefined),
  /** Quotas V2 segment filter: encoded `groupId:segmentId` values (see `encodeSegmentFilter`). */
  quotaSegments: z
    .array(z.string())
    .optional()
    .transform((values): string[] | undefined => {
      const valid = values?.filter((value) => isEncodedSegmentFilter(value));
      return valid?.length ? valid : undefined;
    })
    .catch(undefined),
  /** Free-text search across name + job title. Empty/whitespace collapses to undefined. */
  q: z.coerce.string().trim().min(1).optional().catch(undefined),
  /** One-shot deep link: surveyResponseId to preselect on the Payments tab; stripped after consume. */
  pay: z.string().optional().catch(undefined),
  /**
   * Payments tab payout-count filter. Default "No payments" (`none`) is dropped
   * from the URL; `all` / `1`..`5` are explicit. Unknown values fall back to
   * the default at the read site.
   */
  payout: payoutSearchSchema,
  /**
   * Dev-only opt-in flag. Set `?dev=true` in the URL to reveal hidden
   * developer affordances (currently: a "re-trigger feature extraction"
   * dropdown on Results-tab rows). Any other value is ignored.
   */
  dev: z.literal(true).optional().catch(undefined),
  /**
   * Feature schema version to read the Insights tab at. The pin when absent;
   * otherwise any version the pipeline has (see `FeaturesSchemaVersionBar`,
   * which warns about one it does not).
   */
  version: z.coerce.number().int().positive().optional().catch(undefined),
  /** Chat tab: the open thread. Absent opens a new chat. */
  thread: z.string().min(1).optional().catch(undefined),
});

function isTabId(value: string): value is TabId {
  return (ALL_TAB_IDS as readonly string[]).includes(value);
}

/** Localized tab labels. Evaluated at call time so the active locale applies.
 *  Shared by the tab strip and the document `<title>` (which becomes the print
 *  header and browser-tab label). */
function tabLabels(): Record<TabId, string> {
  return {
    participants: t`Participants`,
    calendar: t`Calendar`,
    bookings: t`Bookings`,
    results: t`Results`,
    details: t`Details`,
    chat: t`Ask`,
    clips: t`Clips`,
    concepts: t`Concepts`,
    insights: t`Insights`,
    messages: t`Messages`,
    completes: t`Completes`,
    payments: t`Payments`,
    analytics: t`Analytics`,
  };
}

function useTabs(
  allowedTabs: readonly TabId[] | null,
  badges: Partial<Record<TabId, number | undefined>>,
): { id: TabId; label: string; badge?: number }[] {
  return useMemo(() => {
    if (!allowedTabs) return [];
    const labels = tabLabels();
    return allowedTabs.map((id) => ({
      id,
      label: labels[id],
      badge: badges[id],
    }));
  }, [allowedTabs, badges]);
}

export const Route = createFileRoute(
  '/researchers/_authenticated/projects/$projectId/details',
)({
  validateSearch: detailsSearchSchema,
  beforeLoad: async ({ context, params }) => {
    const organizationId = context.activeDashboard.organization ?? undefined;
    const teamId = context.activeDashboard.team ?? undefined;

    if (context.v3Enabled !== true) {
      const env = await getPublicEnv();
      if (env.WEBSITE_URL.length > 0 && organizationId && teamId) {
        const legacyDashboardUrl = `${env.WEBSITE_URL}/dashboard/organizations/${organizationId}/teams/${teamId}/projects/${params.projectId}`;
        throw redirect({ href: legacyDashboardUrl });
      }
    }
  },
  // Resolve the project name for the document `<title>`. Reuses the same cached
  // query the component reads, so this dedupes (no extra request) and the title
  // can name the project. Returns `null` on failure so `head` falls back.
  loader: async ({ context, params }) => {
    const result = await context.queryClient.ensureQueryData(
      researcherProjectQueryOptions(params.projectId, context.user?.id),
    );
    return { projectName: result.success ? result.data.name : null };
  },
  component: DetailsPageWrapper,
  // Title is the project's own name, scoped by the active tab (e.g.
  // "Insights · Acme onboarding study"), so the browser tab and the printed
  // page header name the project and section being viewed. Falls back to a
  // generic title if the project name isn't available yet.
  head: ({ match, loaderData }) => {
    const label = tabLabels()[match.search.tab];
    const projectName = loaderData?.projectName;
    return {
      meta: [
        {
          title: projectName
            ? t`${label} · ${projectName}`
            : t`${label} · Project details`,
        },
      ],
    };
  },
});

function DetailsPageWrapper() {
  const { projectId } = Route.useParams();
  const { user, env, isAdminImpersonating } = Route.useRouteContext();
  return (
    <DetailsPage
      projectId={projectId}
      userId={user.id}
      websiteUrl={env.WEBSITE_URL}
      pusherAppKey={env.PUSHER_APP_KEY}
      isAdmin={isAdminImpersonating || user.roles.includes('admin')}
    />
  );
}

function DetailsPage({
  projectId,
  userId,
  websiteUrl,
  pusherAppKey,
  isAdmin,
}: {
  projectId: string;
  userId: string;
  websiteUrl: string;
  pusherAppKey: string;
  /** The dev tools call admin-only endpoints, so only an admin is shown them. */
  isAdmin: boolean;
}) {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const isDev = isAdmin && search.dev === true;
  const analytics = useAnalytics();

  // Profile drawer, open for a user id. Openers that already have the
  // ScreenerResponse row pass it as `snapshot` so the drawer renders
  // immediately; bare Insights citations open with just the id and wait for
  // the by-user-id fetch.
  const [viewingProfile, setViewingProfile] = useState<{
    userId: string;
    snapshot?: ScreenerResponse;
  } | null>(null);
  // Applicant open in the floating detail pane on the Participants tab.
  const [selectedApplicantId, setSelectedApplicantId] = useState<string | null>(
    null,
  );
  const [viewingParticipant, setViewingParticipant] = useState<{
    id: string;
    name: string;
    number: number;
    focusEntryId?: string;
  } | null>(null);
  const [isAddCardOpen, setIsAddCardOpen] = useState(false);
  // Informational only: keep an explicit dismissal local to this project view
  // without changing the project's auto-pay automation.
  const [isAutoPayBannerDismissed, setIsAutoPayBannerDismissed] =
    useState(false);
  const queryClient = useQueryClient();

  const {
    data: projectResult,
    isPending: projectPending,
    isError: projectErrored,
  } = useQuery(researcherProjectQueryOptions(projectId, userId));
  const project = projectResult?.success ? projectResult.data : null;
  const quotasV2 = useQuotasV2(project);

  // Availability gate: moderated studies can't invite respondents until the
  // researcher has an active calendar (or a Calendly integration). Loaded here
  // so the Invite button in the layout can be disabled accordingly.
  const { data: calendars, isLoading: calendarsLoading } =
    useResearcherCalendars(projectId, userId);

  const page = search.page ?? DEFAULT_PAGE;
  const statusFilter = useMemo<ReadonlySet<ParticipantStatusFilter>>(
    () => new Set(search.statuses),
    [search.statuses],
  );
  const genderFilter = useMemo<ReadonlySet<ParticipantGenderFilter>>(
    () => new Set(search.genders),
    [search.genders],
  );
  const ageFilter = useMemo<ReadonlySet<ParticipantAgeFilter>>(
    () => new Set(search.ageGroups),
    [search.ageGroups],
  );
  const locationFilter = useMemo<ReadonlySet<CountryCode>>(
    () => new Set(search.countries),
    [search.countries],
  );
  // Only a project using Quotas V2 filters by segment; a leftover
  // `?quotaSegments=` elsewhere must not count as an active filter either.
  const quotaSegmentFilter = useMemo<ReadonlySet<string>>(
    () => new Set(quotasV2.isInUse ? search.quotaSegments : undefined),
    [quotasV2.isInUse, search.quotaSegments],
  );

  const sortBy =
    search.sort === 'RECENT'
      ? ScreenerResponseSortField.CREATED_AT
      : ScreenerResponseSortField.SCORE;

  const payoutFilter: PayoutCountFilterValue = parsePayoutFilterValue(
    search.payout,
  );

  const apiFilters = toParticipantApiFilters(search);

  const {
    applicants,
    totalResults,
    rosterCount,
    availableCountries,
    hasNonMarketplaceParticipants,
    isLoading: applicantsLoading,
    isError: applicantsErrored,
    isPlaceholderData: applicantsIsPlaceholder,
    hasNextPage: applicantsHaveMore,
    isFetchingNextPage: applicantsFetchingMore,
    fetchNextPage: fetchMoreApplicants,
  } = useInfiniteParticipantsTable(projectId, userId, {
    statuses: apiFilters.statuses,
    sortBy,
    q: search.q,
    view: apiFilters.view,
    qualified: apiFilters.qualified,
    rejected: apiFilters.rejected,
    genders: search.genders,
    ageGroups: search.ageGroups,
    countries: search.countries,
    // The segment filter only exists while the project uses V2 quotas; a
    // leftover `?quotaSegments=` (shared link, V1 project, flag off) would
    // otherwise empty the roster with no visible filter to explain it.
    quotaSegments: quotasV2.isInUse ? search.quotaSegments : undefined,
  });

  // Invitations with no application yet — the participants tab lists them in
  // their own panel. Separate query on purpose: they are not applicants and
  // must not be paged, filtered or counted with them.
  const {
    data: pendingInvitations,
    isError: pendingInvitationsErrored,
    refetch: refetchPendingInvitations,
  } = useProjectInvitations(projectId, userId);

  // Fetch the next chunk as the virtualized table nears the end of what's
  // loaded. Guarded here so the tab can call it unconditionally.
  const loadMoreApplicants = useCallback(() => {
    if (applicantsHaveMore && !applicantsFetchingMore) {
      void fetchMoreApplicants();
    }
  }, [applicantsHaveMore, applicantsFetchingMore, fetchMoreApplicants]);

  const qualificationFilter = effectiveQualification({
    qual: search.qual,
    view: search.view,
  });

  // The opener's snapshot goes stale if a conversation appears after the
  // drawer opens (e.g. the participant sends their first message live).
  // Prefer the row re-fetched by user id — kept fresh by the realtime
  // `newmessage` invalidation — so the drawer's Message tab picks up a newly
  // created conversation instead of starting a duplicate.
  const { data: freshProfileApplicant } = useResearcherApplicantByUserId(
    projectId,
    userId,
    viewingProfile?.userId,
  );
  const profileApplicant =
    freshProfileApplicant ?? viewingProfile?.snapshot ?? null;

  const { data: quota } = useQuotaQuery(
    projectId,
    project ? isQuantitativeStudy(project.kindOfResearch) : false,
  );
  const projectIsAiMod = project ? isAiModStudy(project.kindOfResearch) : false;

  const versionView = useFeaturesSchemaVersionView(project, isAdmin);
  const { featuresSchemaVersion } = versionView;

  // Insights chat is a NEW AI interaction, so besides its PostHog flag it
  // needs the project's organization not to have opted out of AI. The
  // study's existing insights report and results stay reachable: they are
  // historical output, not an AI entry point.
  const { data: canUseAi = false } = useQuery(
    organizationCanUseAiQueryOptions(project?.organizationId),
  );
  const insightsChatEnabled =
    useFeatureFlag('insights_chat').isEnabled && canUseAi;

  const allowedTabs = useMemo(
    () =>
      project
        ? getAllowedTabs(project, {
            hasQuota: !!quota,
            hasInsightsChat: insightsChatEnabled,
          })
        : null,
    [project, quota, insightsChatEnabled],
  );
  // Resolve the tab the user actually sees: an invalid or disallowed
  // `search.tab` falls back to the participants panel. Gate coverage on this
  // resolved value (not the raw search param) so deep links to a disallowed
  // tab still fetch coverage and can show the add-card banner.
  const currentTab: TabId =
    allowedTabs && allowedTabs.includes(search.tab)
      ? search.tab
      : 'participants';
  const fillsViewport = VIEWPORT_FILLING_TABS.has(currentTab);

  const inviteCoverage = useInviteCoverage(
    project ?? { id: '', organizationId: '', teamId: '' },
    { enabled: !!project && currentTab === 'participants', userId },
  );

  // Tab-badge sources fetched at page level so badges stay live regardless of
  // which sub-tab is mounted. Both hooks are gated on a loaded project's ids.
  const teamId = project?.teamId;

  // Gates the "Invite past participants" CTA — only teams whose plan carries the
  // Participant Database capability can reach the new database page.
  const { hasParticipantDatabase } = useTeamCapabilities(teamId);

  // Gates the follow-up pair in the header overflow menu ("Create a follow-up
  // study" / "Add to a program"). Same org flag and cache entry the sidebar
  // and the programs area resolve, so this is usually a cache read.
  const { data: programsEnabled = false } = useQuery(
    // Inlined name — `FeatureFlagConstants` is runtime-only in core-api and
    // never reaches the generated client.
    isomorphicOrgFeatureFlagQueryOptions(
      project?.organizationId,
      'research-programs',
      userId,
    ),
  );

  const { data: unreadCountsData } = useProjectUnreadCounts(
    teamId,
    project ? [projectId] : [],
    userId,
  );
  const unreadCount = unreadCountsData?.projectUnreadCounts[projectId];

  // Keep the Messages tab badge live: refetch the unread count when the
  // messaging service broadcasts a new message on this project's channel.
  useProjectMessagesRealtime({
    appKey: pusherAppKey,
    teamId,
    projectIds: project ? [projectId] : [],
  });

  // Keep the rest of the page live: refetch the project, applicants,
  // bookings, and completes when the backend broadcasts changes (new
  // applicant, status change, project edit, booking, complete).
  useProjectDetailsRealtime({ appKey: pusherAppKey, projectId });

  const { data: paymentsOverview } = useProjectPaymentsOverview(
    teamId,
    projectId,
    userId,
  );

  // Badge denominator and empty-state discriminator: the project's whole
  // roster, independent of the active view and filters. Rides along on the list
  // response, so switching filters never costs an extra count request.
  const totalParticipantCount = rosterCount ?? 0;
  const totalCountUnknown = applicantsLoading || applicantsErrored;

  // Results-tab badge: prefetch page 1 of the interview-response list (only
  // AI-mod studies have a Results tab) and read its `totalResults`. This is the
  // SAME query the Results tab issues for page 1 — keeping page/pageSize/
  // extractedOnly identical means react-query serves both from one request and
  // pre-warms the tab, instead of a duplicate count fetch. If the Results tab's
  // page-1 query shape ever changes, update this call to match or the dedupe
  // silently breaks.
  const {
    data: resultsPageOne,
    isLoading: resultsCountLoading,
    isError: resultsCountErrored,
  } = useResearcherAllInterviewResponses(projectId, userId, {
    enabled: projectIsAiMod,
    extractedOnly: false,
  });
  const resultsCount = resultsPageOne?.totalResults;
  const resultsCountUnknown = resultsCountLoading || resultsCountErrored;

  // Completes-tab badge: prefetch page 1 of the completes list (only online-task
  // studies have a Completes tab) and read its `totalResults`. This is the SAME
  // query the Completes tab issues for page 1 — keeping page/taskType identical
  // means react-query serves both from one request and pre-warms the tab, and
  // the badge always equals the list count (not the denormalized `taskCompleted`
  // metric). If the Completes tab's page-1 query shape ever changes, update this
  // call to match or the dedupe silently breaks.
  const projectIsOnlineTask = project ? isOnlineTaskProject(project) : false;
  const {
    data: completesPageOne,
    isLoading: completesCountLoading,
    isError: completesCountErrored,
  } = useResearcherCompletes(
    projectId,
    // Gated off for non-online-task projects; the fallback only keeps the call
    // type-safe (online-task projects always have a task type).
    project?.task?.type ?? 'lookback',
    userId,
    1,
    projectIsOnlineTask,
  );
  const completesCount = completesPageOne?.totalResults;
  const completesCountUnknown = completesCountLoading || completesCountErrored;

  // Bookings-tab badge: count the rendered SCHEDULED-only list (shares the tab's
  // query key, so it dedupes and pre-warms the tab). Moderated studies only.
  const projectIsModerated = project
    ? isModeratedStudy(project.kindOfResearch)
    : false;
  const { data: bookingsData } = useProjectBookings(
    projectId,
    userId,
    projectIsModerated,
  );
  const bookingsCount = useMemo(
    () =>
      bookingsData
        ? selectConfirmedBookings(bookingsData.results ?? []).length
        : undefined,
    [bookingsData],
  );

  const setPage = useCallback(
    (next: number) => {
      // The open applicant won't be on the next page — close the pane.
      setSelectedApplicantId(null);
      void navigate({
        search: (prev) => ({ ...prev, page: next }),
        replace: true,
      });
    },
    [navigate],
  );

  const selectApplicant = useCallback(
    (applicant: ScreenerResponse) => setSelectedApplicantId(applicant.id),
    [],
  );
  const closePane = useCallback(() => setSelectedApplicantId(null), []);

  const setStatusFilter = useCallback(
    (next: Set<ParticipantStatusFilter>) => {
      analytics?.ui({
        action: 'filtered',
        actionSubject: 'participants',
        source: 'participantsTab',
        attributes: {
          // Comma-joined: GA4 has no array param. Status enum values only — no PII.
          selectedStatuses: [...next].join(','),
          statusCount: next.size,
          // An empty selection is the "Clear selection" / deselect-all outcome.
          cleared: next.size === 0,
        },
      });
      // The roster changes (and page resets to 1) — the open applicant may no
      // longer be present, so close the pane, as page/tab changes already do.
      setSelectedApplicantId(null);
      void navigate({
        search: (prev) => nextSearchOnStatusFilterChange(prev, next),
        replace: true,
      });
    },
    [analytics, navigate],
  );

  const setVisibilityFilter = useCallback(
    (next: ParticipantVisibilityFilter) => {
      // Same as the status filter: the roster changes and page resets, so the
      // open applicant may no longer be present — close the pane.
      setSelectedApplicantId(null);
      void navigate({
        search: (prev) => nextSearchOnVisibilityFilterChange(prev, next),
        replace: true,
      });
    },
    [navigate],
  );

  const setQualificationFilter = useCallback(
    (next: ParticipantQualificationFilter) => {
      // Same as the status filter: the roster changes and page resets, so the
      // open applicant may no longer be present — close the pane.
      setSelectedApplicantId(null);
      void navigate({
        search: (prev) => nextSearchOnQualificationFilterChange(prev, next),
        replace: true,
      });
    },
    [navigate],
  );

  const setGenderFilter = useCallback(
    (next: Set<ParticipantGenderFilter>) => {
      setSelectedApplicantId(null);
      void navigate({
        search: (prev) => nextSearchOnGenderFilterChange(prev, next),
        replace: true,
      });
    },
    [navigate],
  );

  const setAgeFilter = useCallback(
    (next: Set<AgeGroup>) => {
      setSelectedApplicantId(null);
      void navigate({
        search: (prev) => nextSearchOnAgeFilterChange(prev, next),
        replace: true,
      });
    },
    [navigate],
  );

  const setLocationFilter = useCallback(
    (next: Set<CountryCode>) => {
      setSelectedApplicantId(null);
      void navigate({
        search: (prev) => nextSearchOnLocationFilterChange(prev, next),
        replace: true,
      });
    },
    [navigate],
  );

  const setQuotaSegmentFilter = useCallback(
    (next: Set<string>) => {
      setSelectedApplicantId(null);
      void navigate({
        search: (prev) => nextSearchOnQuotaSegmentFilterChange(prev, next),
        replace: true,
      });
    },
    [navigate],
  );

  const setPayoutFilter = useCallback(
    (next: PayoutCountFilterValue) => {
      // The Payments tab has no detail pane, so there's no applicant selection
      // to clear here (unlike the participant filters).
      void navigate({
        search: (prev) => nextSearchOnPayoutFilterChange(prev, next),
        replace: true,
      });
    },
    [navigate],
  );

  const setSort = useCallback(
    (next: 'QUALIFIED' | 'RECENT') => {
      analytics?.ui({
        action: 'sorted',
        actionSubject: 'participants',
        source: 'participantsTab',
        attributes: {
          sortField: next === 'QUALIFIED' ? 'screener_score' : 'date_applied',
          // Both columns are descending-only server-side.
          sortDir: 'desc',
        },
      });
      setSelectedApplicantId(null);
      void navigate({
        search: (prev) => ({
          ...prev,
          sort: next === 'QUALIFIED' ? undefined : next,
          page: 1,
        }),
        replace: true,
      });
    },
    [analytics, navigate],
  );

  // The query a user just submitted via the search box, awaiting its result
  // count. Read by the effect below once the matching fetch settles — that's the
  // only point we know `result_count`. Null when no user search is pending, so
  // deep-links / initial loads don't emit a `participants_searched` event.
  const pendingSearchRef = useRef<string | null>(null);

  const setSearch = useCallback(
    (next: string) => {
      pendingSearchRef.current = next;
      setSelectedApplicantId(null);
      void navigate({
        search: (prev) => ({
          ...prev,
          q: next || undefined,
          page: 1,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  // Strips the one-shot `pay` deep link once the Payments tab has consumed it.
  const clearPayParam = useCallback(() => {
    void navigate({
      search: (prev) => ({ ...prev, pay: undefined }),
      replace: true,
    });
  }, [navigate]);

  const activeTab = search.tab;

  // Emit `participants_searched` once the fetch for a user-submitted query
  // settles — that's when `result_count` is known. Depending on
  // `applicantsResult` (a fresh object per query key) guarantees the effect
  // re-runs when the new results land, even when served instantly from cache.
  // PRIVACY: the query string is never logged — only whether one was entered.
  useEffect(() => {
    if (pendingSearchRef.current === null) return;
    if (applicantsLoading || applicantsIsPlaceholder) return;
    // A failed request reports `totalResults` as 0 with no data behind it —
    // don't record that as a genuine "0 results" search. Leave the pending
    // ref set so a subsequent successful refetch still emits the event.
    if (applicantsErrored) return;
    const query = pendingSearchRef.current;
    pendingSearchRef.current = null;
    analytics?.ui({
      action: 'searched',
      actionSubject: 'participants',
      source: 'participantsTab',
      attributes: {
        hasQuery: query.length > 0,
        resultCount: totalResults,
      },
    });
    // `applicants` (a fresh array per fetch) stands in for the old
    // result-object dependency: it changes when new results land, even when
    // served instantly from cache.
  }, [
    analytics,
    applicants,
    totalResults,
    applicantsLoading,
    applicantsIsPlaceholder,
    applicantsErrored,
  ]);

  // Fire `project_workspace_opened` once per project — the entry marker for the
  // manage-project area. Waits until the project and its participant count have
  // loaded so `participantCount` is real, then locks to that projectId so a
  // refetch / count update can't re-fire it.
  // NOTE: `auto_accept_enabled` from the tracking plan is intentionally omitted
  // — the governing project setting isn't exposed on the web `Project` type.
  const workspaceOpenedForRef = useRef<string | null>(null);
  const projectStatus = project?.status;
  useEffect(() => {
    if (!project) return;
    if (totalCountUnknown) return;
    if (workspaceOpenedForRef.current === projectId) return;
    workspaceOpenedForRef.current = projectId;
    analytics?.track({
      action: 'opened',
      actionSubject: 'projectWorkspace',
      attributes: {
        projectId,
        projectStatus,
        participantCount: totalParticipantCount,
        activeTab,
      },
    });
  }, [
    analytics,
    project,
    projectId,
    projectStatus,
    totalCountUnknown,
    totalParticipantCount,
    activeTab,
  ]);

  const badges = useMemo(
    () =>
      project
        ? computeTabBadges({
            project,
            participantCount: totalCountUnknown
              ? undefined
              : totalParticipantCount,
            resultsCount: resultsCountUnknown ? undefined : resultsCount,
            completesCount: completesCountUnknown ? undefined : completesCount,
            bookingsCount,
            unreadCount,
            paymentsOverview,
          })
        : {},
    [
      project,
      totalCountUnknown,
      totalParticipantCount,
      resultsCount,
      resultsCountUnknown,
      completesCount,
      completesCountUnknown,
      bookingsCount,
      unreadCount,
      paymentsOverview,
    ],
  );
  const tabs = useTabs(allowedTabs, badges);

  const handleTabChange = useCallback(
    (tab: Key) => {
      const tabStr = String(tab);
      if (!isTabId(tabStr) || !allowedTabs?.includes(tabStr)) return;
      analytics?.ui({
        action: 'switched',
        actionSubject: 'projectWorkspaceTab',
        actionSubjectId: tabStr,
        source: 'projectWorkspace',
        attributes: { tab: tabStr },
      });
      setSelectedApplicantId(null);
      void navigate({
        // `page` is shared by the paginated tabs — reset it to 1 when switching
        // to one (so a deep page in the previous tab doesn't carry over) and
        // drop it from the URL entirely on tabs that don't paginate.
        search: (prev) => ({
          ...prev,
          tab: tabStr,
          page: isPaginatedTab(tabStr) ? 1 : undefined,
          // one-shot deep link: a manual tab switch discards it.
          pay: undefined,
        }),
      });
    },
    [analytics, navigate, allowedTabs],
  );

  const projectAction = useProjectAction(project?.teamId);
  const isClosePending =
    projectAction.isPending && projectAction.variables?.action === 'close';
  const isArchivePending =
    projectAction.isPending && projectAction.variables?.action === 'archive';
  const isPausePending =
    projectAction.isPending && projectAction.variables?.action === 'pause';
  const isCopyPending =
    projectAction.isPending && projectAction.variables?.action === 'copy';

  const closeProject = useCallback(
    // onSettled fires on failure too: the outcome is reported by a toast, which
    // sits behind the modal overlay, so leaving the modal open on a rejected
    // close hides the reason and strands the researcher on a dead button.
    (message?: string, onSettled?: () => void) => {
      if (projectAction.isPending) return;
      projectAction.mutate(
        { action: 'close', projectId, message },
        {
          onSuccess: () => {
            onSettled?.();
            analytics?.track({ action: 'closed', actionSubject: 'project' });
            notify({ title: t`Project closed`, color: 'success' });
          },
          onError: (error) => {
            onSettled?.();
            console.error('Close project failed', error);
            // The backend rejects closing while participants are still
            // scheduled/attended and unpaid. Metrics-based pre-checks can lag,
            // so map that 400 to actionable copy instead of "try again later"
            // (retrying never helps here).
            const status = getAxiosErrorStatus(error);
            const serverMessage = getAxiosErrorMessage(error);
            const hasUnpaidParticipants =
              status === 400 &&
              !!serverMessage?.includes('unpaid participants');
            notify({
              title: t`Could not close project`,
              description: hasUnpaidParticipants
                ? t`Some participants haven't been paid yet. Pay them, or mark them as not attended, on the Payments tab — then close the project.`
                : status === 400 && serverMessage
                  ? serverMessage
                  : t`Please try again later.`,
              color: 'error',
            });
          },
        },
      );
    },
    [projectAction, projectId, analytics],
  );

  const archiveProject = useCallback(
    (onArchived?: () => void) => {
      if (projectAction.isPending) return;
      projectAction.mutate(
        { action: 'archive', projectId },
        {
          onSuccess: () => {
            onArchived?.();
            notify({ title: t`Project archived`, color: 'success' });
          },
          onError: (error) => {
            console.error('Archive project failed', error);
            notify({
              title: t`Could not archive project`,
              description: t`Please try again later.`,
              color: 'error',
            });
          },
        },
      );
    },
    [projectAction, projectId],
  );

  const pauseProject = useCallback(
    (paused: boolean, onDone?: () => void) => {
      if (projectAction.isPending) return;
      projectAction.mutate(
        { action: 'pause', projectId, paused },
        {
          onSuccess: () => {
            onDone?.();
            if (paused) {
              analytics?.track({
                action: 'recruitingPaused',
                actionSubject: 'project',
              });
            } else {
              analytics?.track({
                action: 'recruitingResumed',
                actionSubject: 'project',
              });
            }
            notify({
              title: paused ? t`Recruitment paused` : t`Recruitment resumed`,
              color: 'success',
            });
          },
          onError: (error) => {
            // Resume is rejected with 400 when the project is at the org hard
            // recruitment cap — surface that server copy so researchers know
            // to contact support rather than retry.
            const status = getAxiosErrorStatus(error);
            const serverMessage = getAxiosErrorMessage(error);
            notify({
              title: paused
                ? t`Could not pause recruitment`
                : t`Could not resume recruitment`,
              description:
                status === 400 && serverMessage
                  ? serverMessage
                  : t`Please try again later.`,
              color: 'error',
            });
          },
        },
      );
    },
    [projectAction, projectId, analytics],
  );

  const copyProject = useCallback(
    (programId: string | undefined, onDone?: () => void) => {
      if (projectAction.isPending) return;
      projectAction.mutate(
        { action: 'copy', projectId, programId },
        {
          onSuccess: (data) => {
            onDone?.();
            notify({ title: t`Project copied`, color: 'success' });
            const newProjectId =
              typeof data === 'object' &&
              data !== null &&
              'id' in data &&
              typeof data.id === 'string'
                ? data.id
                : null;
            if (newProjectId) {
              void navigate({
                to: '/researchers/projects/$projectId/edit',
                params: { projectId: newProjectId },
              });
            }
          },
          onError: (error) => {
            console.error('Copy project failed', error);
            notify({
              title: t`Could not copy project`,
              description: t`Please try again later.`,
              color: 'error',
            });
          },
        },
      );
    },
    [projectAction, projectId, navigate],
  );

  if (projectPending) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <div className="size-6 animate-spin rounded-full border-4 border-secondary border-t-brand-solid" />
      </div>
    );
  }

  if (projectErrored || !project) {
    return (
      <div className="flex min-h-dvh items-center justify-center px-8 text-sm text-tertiary">
        {t`Unable to load project details.`}
      </div>
    );
  }

  const isAiMod = isAiModStudy(project.kindOfResearch);
  const isModerated = isModeratedStudy(project.kindOfResearch);
  const isOnlineTask = isOnlineTaskProject(project);

  // Availability gate — only for study types that schedule against the
  // researcher's calendar. Calendly counts as established. Mirrors the legacy
  // split: the banner clears as soon as *any* calendar exists, but inviting
  // requires a *scheduling-enabled* calendar. Treat as established while
  // calendars load so neither surface flashes its blocked state.
  const availabilityRequired =
    isAvailabilityRequired(project) && !calendarsLoading;
  const hasCalendly = (project.externalScheduling?.calendly ?? []).some(
    (event) => event.active,
  );
  const hasAnyCalendar = (calendars ?? []).length > 0;
  const hasActiveCalendar = (calendars ?? []).some((c) => c.isActive);

  // Participants-tab context banners (legacy `responses.html`): locked add-card,
  // set-availability (hidden while locked), and the auto-pay disclosure. The
  // auto-pay banner is also shown on the Completes tab.
  const showLockedBanner = shouldShowLockedBanner(inviteCoverage, currentTab);

  const showAvailabilityBanner = shouldShowAvailabilityBanner({
    project,
    hasAnyCalendar,
    calendarsLoading,
    locked: showLockedBanner,
    currentTab,
  });

  const showAutoPayBanner = shouldShowAutoPayBanner(project, currentTab);
  // Online-task projects have no scheduled interview, so the auto-pay
  // disclosure describes task completion instead of an interview time.
  const autoPayBannerDescription = isOnlineTask
    ? t`If a participant has completed their task and isn't marked as 'not attended' within 48 hours, we'll proceed with their payment.`
    : t`If a scheduled interview time has passed and the participant isn't marked as 'not attended' within 48 hours, we'll proceed with their payment.`;
  // Floats over the page, so it sits outside <Tabs>: a non-panel child of
  // <Tabs> crashes react-aria's reconciler (see the participants panel).
  const versionBar =
    versionView.showBar &&
    currentTab === 'insights' &&
    versionView.pinnedVersion !== undefined &&
    versionView.viewedVersion !== undefined ? (
      <FeaturesSchemaVersionBar
        projectId={projectId}
        userId={userId}
        pinnedVersion={versionView.pinnedVersion}
        experimentalVersions={versionView.experimentalVersions}
        viewedVersion={versionView.viewedVersion}
        onSelectVersion={versionView.selectVersion}
      />
    ) : null;
  const autoPayBanner =
    showAutoPayBanner && !isAutoPayBannerDismissed ? (
      <AlertFullWidth
        color="brand"
        title={t`Using auto-pay?`}
        description={autoPayBannerDescription}
        onClose={() => setIsAutoPayBannerDismissed(true)}
        showDismissButton={false}
        className="pt-3 pb-2"
      />
    ) : null;

  // Invite: blocked until a calendar can actually take bookings (legacy invite
  // modal required an active calendar). The reason adapts to whether the
  // researcher needs to create a calendar or just enable scheduling on one.
  const inviteDisabled =
    availabilityRequired && !hasActiveCalendar && !hasCalendly;
  const inviteDisabledReason = hasAnyCalendar
    ? t`Enable scheduling on a calendar before you can invite respondents.`
    : t`Set up a calendar to establish your availability before inviting respondents.`;

  const actionFlags = getProjectActionFlags(project, {
    onPause: pauseProject,
    onUnpause: pauseProject,
    onClose: closeProject,
    onArchive: archiveProject,
  });

  // Only open the interview drawer when we can seek to the quote's position;
  // a bare face or citation opens the profile drawer instead of an interview
  // starting at 0:00.
  const viewParticipantFromInsights = (
    id: string,
    name: string,
    number: number,
    focusEntryId?: string,
  ) => {
    if (focusEntryId) {
      setViewingParticipant({ id, name, number, focusEntryId });
    } else {
      setViewingProfile({ userId: id });
    }
  };

  return (
    <ProjectDetailsLayout
      project={project}
      projectId={projectId}
      websiteUrl={websiteUrl}
      inviteDisabled={inviteDisabled}
      inviteDisabledReason={inviteDisabledReason}
      pauseProject={pauseProject}
      isPausePending={isPausePending}
      closeProject={closeProject}
      isClosePending={isClosePending}
      copyProject={copyProject}
      isCopyPending={isCopyPending}
      archiveProject={archiveProject}
      isArchivePending={isArchivePending}
      hasParticipantDatabase={hasParticipantDatabase}
      userId={userId}
      programsEnabled={programsEnabled}
    >
      {/* Play overlay on video/audio avatars, quote overlay on text — every
          surface that opens the interview drawer reads this. */}
      <InterviewDrawerCueProvider
        kind={drawerCueKindFromDeliveryMode(project.interviewDeliveryMode)}
      >
        <Tabs
          selectedKey={currentTab}
          onSelectionChange={handleTabChange}
          className={fillsViewport ? 'min-h-0 flex-1' : undefined}
          // Tells the page's scroller it has nothing to scroll: see the layout.
          data-fills-viewport={fillsViewport ? '' : undefined}
        >
          <Tabs.List
            type="underline"
            size="sm"
            items={tabs}
            className="sticky top-0 z-10 bg-primary px-8 pt-3 print:hidden"
          >
            {(item) => (
              <Tabs.Item id={item.id}>
                {item.label}
                {typeof item.id === 'string' &&
                  isTabId(item.id) &&
                  typeof item.badge === 'number' && (
                    <TabCountBadge tabId={item.id} count={item.badge} />
                  )}
              </Tabs.Item>
            )}
          </Tabs.List>

          <Tabs.Panel
            id="participants"
            className="flex min-h-0 flex-1 flex-col overflow-hidden"
          >
            {/* Inside the panel, not direct <Tabs> children: as Tabs children,
              toggling a banner after an async query settled crashed react-aria's
              reconciler (insertBefore on a non-tab node). */}
            {showLockedBanner && (
              <AlertFullWidth
                color="warning"
                actionType="link"
                title={t`Add a credit card`}
                description={t`Before you can invite respondents, you'll need a credit card on file.`}
                confirmLabel={t`Add credit card`}
                onConfirm={() => setIsAddCardOpen(true)}
                className="bg-warning-25 pt-3 pb-2"
              />
            )}
            {showAvailabilityBanner && (
              <AlertFullWidth
                color="warning"
                actionType="link"
                title={t`Set your availability`}
                description={t`Before you can invite respondents, set up a calendar to establish your availability.`}
                confirmLabel={t`Set up calendar`}
                onConfirm={() => handleTabChange('calendar')}
                className="bg-warning-25 pt-3 pb-2"
              />
            )}
            {autoPayBanner}
            <div className="flex min-h-0 flex-1 flex-col px-8 py-6">
              <ParticipantsTab
                // The follow-up additions (widened checkboxes + "Invite to
                // another project") drive the batch-invite endpoint, which is
                // plan-gated on PARTICIPANT_DATABASE — offering them without
                // the capability would 403 on send.
                programsEnabled={programsEnabled && hasParticipantDatabase}
                isAdmin={isAdmin}
                applicants={applicants}
                pendingInvitations={pendingInvitations}
                pendingInvitationsError={
                  pendingInvitationsErrored && pendingInvitations === undefined
                }
                onRetryPendingInvitations={() =>
                  void refetchPendingInvitations()
                }
                isLoading={applicantsLoading}
                isError={applicantsErrored}
                isPlaceholder={applicantsIsPlaceholder}
                project={project}
                projectId={projectId}
                userId={userId}
                hasMore={applicantsHaveMore ?? false}
                isLoadingMore={applicantsFetchingMore}
                onLoadMore={loadMoreApplicants}
                totalResults={totalResults}
                totalCount={totalParticipantCount}
                statusFilter={statusFilter}
                visibilityFilter={search.view ?? 'all'}
                qualificationFilter={qualificationFilter}
                genderFilter={genderFilter}
                ageFilter={ageFilter}
                locationFilter={locationFilter}
                quotaSegmentFilter={quotaSegmentFilter}
                availableCountries={availableCountries}
                showSourceLabels={hasNonMarketplaceParticipants}
                sort={search.sort ?? 'QUALIFIED'}
                q={search.q ?? ''}
                onStatusFilterChange={setStatusFilter}
                onVisibilityFilterChange={setVisibilityFilter}
                onQualificationFilterChange={setQualificationFilter}
                onGenderFilterChange={setGenderFilter}
                onAgeFilterChange={setAgeFilter}
                onLocationFilterChange={setLocationFilter}
                onQuotaSegmentFilterChange={setQuotaSegmentFilter}
                onSortChange={setSort}
                onSearchChange={setSearch}
                selectedApplicantId={selectedApplicantId}
                onSelectApplicant={selectApplicant}
                onClosePane={closePane}
              />
            </div>
          </Tabs.Panel>

          {isModerated && (
            <Tabs.Panel id="calendar" className="px-8 py-6">
              <CalendarTab
                project={project}
                userId={userId}
                websiteUrl={websiteUrl}
              />
            </Tabs.Panel>
          )}

          {isModerated && (
            <Tabs.Panel id="bookings" className="px-8 py-6">
              <BookingsTab project={project} userId={userId} />
            </Tabs.Panel>
          )}

          {isAiMod && (
            <>
              <Tabs.Panel id="results" className="px-8 py-6">
                <ResultsTab
                  projectId={projectId}
                  userId={userId}
                  enabled={currentTab === 'results'}
                  projectName={project.name}
                  isDev={isDev}
                  devVersion={versionView.viewedVersion}
                  allottedMinutes={
                    project.timeMinutesRequired ?? project.duration
                  }
                  page={page}
                  onPageChange={setPage}
                  onViewParticipant={(id, name, number) => {
                    // Entry point to interview review. No participant identifier —
                    // only which surface opened the drawer (CSV `source`).
                    analytics?.ui({
                      action: 'opened',
                      actionSubject: 'interviewDrawer',
                      source: 'results_row',
                    });
                    setViewingParticipant({ id, name, number });
                  }}
                />
              </Tabs.Panel>

              <Tabs.Panel id="details" className="px-8 py-6">
                <DetailsTab
                  projectId={projectId}
                  userId={userId}
                  enabled={currentTab === 'details'}
                  isDev={isDev}
                  featuresSchemaVersion={featuresSchemaVersion}
                  onViewParticipant={(id, name, number, focusEntryId) => {
                    // Same drawer, opened from a participant photo in the Details
                    // distribution charts. No identifier — only the source.
                    analytics?.ui({
                      action: 'opened',
                      actionSubject: 'interviewDrawer',
                      source: 'details_distribution',
                    });
                    setViewingParticipant({ id, name, number, focusEntryId });
                  }}
                />
              </Tabs.Panel>

              {allowedTabs?.includes('chat') && (
                // min-h-0 + flex: the chat fills the viewport below the tab
                // strip and scrolls its panes internally, inside the same
                // page padding as the other tabs.
                <Tabs.Panel
                  id="chat"
                  className="flex min-h-0 flex-1 flex-col px-8 py-6"
                >
                  <InsightsChatTab
                    projectId={projectId}
                    userId={userId}
                    enabled={currentTab === 'chat'}
                    featuresSchemaVersion={featuresSchemaVersion}
                    renderParticipant={(view, panel) => (
                      <ParticipantSideView
                        view={view}
                        panel={panel}
                        project={project}
                        userId={userId}
                      />
                    )}
                  />
                </Tabs.Panel>
              )}

              {allowedTabs?.includes('clips') && (
                <Tabs.Panel id="clips" className="px-8 pt-10 pb-6">
                  <ClipsTab
                    projectId={projectId}
                    userId={userId}
                    enabled={currentTab === 'clips'}
                    featuresSchemaVersion={featuresSchemaVersion}
                    onViewParticipant={(id) => {
                      setViewingProfile({ userId: id });
                    }}
                  />
                </Tabs.Panel>
              )}

              <Tabs.Panel id="concepts" className="px-8 py-6">
                <ConceptsTab
                  projectId={projectId}
                  userId={userId}
                  enabled={currentTab === 'concepts'}
                  featuresSchemaVersion={featuresSchemaVersion}
                  onViewParticipant={(id, name, number, focusEntryId) => {
                    // Same rule as Insights: only open the interview drawer when
                    // we can seek to the quote, otherwise the profile drawer.
                    if (focusEntryId) {
                      setViewingParticipant({ id, name, number, focusEntryId });
                    } else {
                      setViewingProfile({ userId: id });
                    }
                  }}
                />
              </Tabs.Panel>

              {/* p-0: the Insights report paints its own full-bleed gray canvas.
                min-h-0 + flex: the report scrolls itself, beside its side panel, so
                the panel's scrollbar and the report's sit side by side. */}
              <Tabs.Panel
                id="insights"
                className="flex min-h-0 flex-1 flex-col p-0 print:block"
              >
                <InsightsReportPanel
                  projectName={project.name}
                  studyType={
                    getTypeConfig(
                      project.kindOfResearch,
                      project.researchConductType,
                    )?.label
                  }
                  projectId={projectId}
                  userId={userId}
                  enabled={currentTab === 'insights'}
                  featuresSchemaVersion={featuresSchemaVersion}
                  onViewParticipant={viewParticipantFromInsights}
                  renderParticipant={(view, panel) => (
                    <ParticipantSideView
                      view={view}
                      panel={panel}
                      project={project}
                      userId={userId}
                    />
                  )}
                />
              </Tabs.Panel>
            </>
          )}

          {/* min-h-0 + flex: the inbox fills the viewport below the tab strip
            and scrolls its panes internally. */}
          <Tabs.Panel id="messages" className="flex min-h-0 flex-1 flex-col">
            <MessagesTab
              projectId={projectId}
              userId={userId}
              enabled={currentTab === 'messages'}
              teamId={teamId}
            />
          </Tabs.Panel>

          {allowedTabs?.includes('completes') && (
            <Tabs.Panel id="completes" className="p-0">
              {autoPayBanner}
              <div className="px-8 py-6">
                <CompletesTab
                  project={project}
                  projectId={projectId}
                  userId={userId}
                  page={page}
                  onPageChange={setPage}
                  onSelectApplicant={(applicant) =>
                    setViewingProfile({
                      userId: applicant.userId,
                      snapshot: applicant,
                    })
                  }
                  enabled={currentTab === 'completes'}
                />
              </div>
            </Tabs.Panel>
          )}

          <Tabs.Panel id="payments" className="p-0">
            <PaymentsTab
              organizationId={project.organizationId}
              teamId={project.teamId}
              projectId={projectId}
              userId={userId}
              pauseProject={pauseProject}
              isPausePending={isPausePending}
              canPause={actionFlags.canPause}
              canUnpause={actionFlags.canUnpause}
              canClose={actionFlags.canOfferClose}
              closeProject={closeProject}
              isClosePending={isClosePending}
              unpaidParticipantCount={actionFlags.unpaidParticipantCount}
              canArchive={actionFlags.canArchive}
              archiveProject={archiveProject}
              isArchivePending={isArchivePending}
              payParticipantId={search.pay}
              onPayParticipantConsumed={clearPayParam}
              payoutFilter={payoutFilter}
              onPayoutFilterChange={setPayoutFilter}
            />
          </Tabs.Panel>

          <Tabs.Panel id="analytics" className="p-0">
            <AnalyticsTab
              projectId={projectId}
              countries={project.countries ?? []}
              ageRange={ageRangeFromProject(project)}
              enabled={currentTab === 'analytics'}
            />
          </Tabs.Panel>
        </Tabs>

        {versionBar}

        <InterviewResponsesDrawer
          isOpen={!!viewingParticipant}
          onClose={() => setViewingParticipant(null)}
          projectId={projectId}
          projectName={project.name}
          participantUserId={viewingParticipant?.id ?? ''}
          participantName={viewingParticipant?.name ?? ''}
          participantNumber={viewingParticipant?.number}
          interviewDeliveryMode={project.interviewDeliveryMode}
          studyLanguage={project.studyLanguage}
          focusEntryId={viewingParticipant?.focusEntryId}
          userId={userId}
          onShowProfile={
            viewingParticipant
              ? () =>
                  setViewingProfile({
                    userId: viewingParticipant.id,
                    snapshot: applicants.find(
                      (a) => a.userId === viewingParticipant.id,
                    ),
                  })
              : undefined
          }
        />

        <ScreenerResponsesDrawer
          isOpen={!!viewingProfile}
          onClose={() => setViewingProfile(null)}
          applicant={profileApplicant}
          project={project}
          userId={userId}
          defaultTab="profile"
        />

        {isAddCardOpen && (
          <AddCreditCardModal
            isOpen
            onOpenChange={setIsAddCardOpen}
            teamId={project.teamId}
            projectId={projectId}
            onCardSaved={() => {
              setIsAddCardOpen(false);
              void queryClient.invalidateQueries({
                queryKey: queryKeys.creditCards.byTeamId(project.teamId),
              });
              void queryClient.invalidateQueries({
                queryKey: queryKeys.pricing.teamPlanSummary(project.teamId),
              });
            }}
          />
        )}
      </InterviewDrawerCueProvider>
    </ProjectDetailsLayout>
  );
}
